import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const from = location?.state?.from || null;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({}); // Clear previous errors

    try {
      const { data, profile, error } = await signIn(
        formData?.email,
        formData?.password
      );

      if (error) {
        setErrors({
          submit: error?.message || 'Invalid credentials. Please check your email and password.'
        });
        setIsLoading(false);
        return;
      }

      // Admin portal only allows owner and contributor roles
      if (profile?.role === 'owner') {
        // Check if this is first-time login for owner
        navigate(from || '/owner-dashboard');
      } else if (profile?.role === 'contributor') {
        // Check if this is first-time login for contributor
        const isFirstLogin = !profile?.full_name || profile?.full_name?.trim() === '';
        
        if (isFirstLogin) {
          // Redirect to first-time setup
          navigate('/contributor-setup', { state: { isFirstLogin: true } });
        } else {
          navigate(from || '/contributor-dashboard');
        }
      } else if (profile?.role === 'user') {
        // Users should not access admin portal - sign out
        await supabase?.auth?.signOut();
        setErrors({ 
          submit: 'This portal is for owners and contributors only. Please use the visitor login.' 
        });
      } else {
        // Unknown role - sign out
        await supabase?.auth?.signOut();
        setErrors({ submit: 'Invalid account type.' });
      }
    } catch (error) {
      setErrors({
        submit: error?.message || 'An error occurred during login. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <form onSubmit={handleLogin} className="space-y-6">
        <Input
          label="Email Address"
          type="email"
          placeholder="your.email@example.com"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
        />

        <Input
          label="Password"
          type="password"
          placeholder="Enter your password"
          value={formData?.password}
          onChange={(e) => handleInputChange('password', e?.target?.value)}
          error={errors?.password}
          required
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox
              id="rememberMe"
              checked={formData?.rememberMe}
              onChange={(checked) => handleInputChange('rememberMe', checked)}
            />
            <label htmlFor="rememberMe" className="text-sm text-muted-foreground cursor-pointer">
              Remember me
            </label>
          </div>
          <button
            type="button"
            className="text-sm text-primary hover:underline"
            onClick={() => {}}
          >
            Forgot password?
          </button>
        </div>

        {errors?.submit && (
          <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-sm text-destructive">{errors?.submit}</p>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          className="w-full"
          loading={isLoading}
          iconName="LogIn"
          iconPosition="right"
        >
          Sign In
        </Button>
      </form>

      <div className="mt-8 p-4 rounded-xl bg-muted/50 border border-border">
        <p className="text-xs font-medium text-muted-foreground mb-2">Demo Credentials:</p>
        <div className="space-y-1 text-xs text-muted-foreground">
          <p><span className="font-medium">Owner:</span> christian@radiavision.com / Owner@2026</p>
          <p><span className="font-medium">Contributor:</span> contributor@radiavision.com / Contributor@2026</p>
        </div>
        <div className="mt-3 p-2 bg-warning/10 border border-warning/20 rounded">
          <p className="text-xs text-warning">⚠️ Demo accounts must be created manually in Supabase Dashboard. See README.md for setup instructions.</p>
        </div>
      </div>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to Welcome Page
        </button>
      </div>
    </div>
  );
};

export default LoginForm;